import { Link } from "react-router-dom";

const DEFAULT_OVERLAY = 0.55;

function focalPointStyle(focalPoint) {
  const xNum = Number(focalPoint?.x);
  const yNum = Number(focalPoint?.y);
  const x = Number.isFinite(xNum) ? Math.max(0, Math.min(100, xNum)) : 50;
  const y = Number.isFinite(yNum) ? Math.max(0, Math.min(100, yNum)) : 50;
  return `${x}% ${y}%`;
}

function ctaClasses(variant) {
  const base =
    "inline-flex items-center justify-center gap-3 rounded-md px-6 py-4 text-sm font-bold uppercase tracking-[0.16em] transition";
  if (variant === "outline") {
    return `${base} border border-white/45 text-white hover:bg-white hover:text-[#14201f]`;
  }
  return `${base} bg-[#f5c45e] text-[#201711] hover:bg-[#ffd77c]`;
}

function CtaButton({ cta }) {
  const { label, href, variant } = cta;
  const className = ctaClasses(variant);
  if (typeof href === "string" && href.startsWith("/")) {
    return (
      <Link to={href} className={className}>
        {label}
      </Link>
    );
  }
  return (
    <a href={href} className={className}>
      {label}
    </a>
  );
}

/**
 * Shared CMS-driven page banner. Standardized height across all in-scope
 * pages — never used on Vedant or Home (those keep bespoke heroes).
 * Pure presentation: callers compose, this renders.
 */
export default function PageBanner({
  eyebrow,
  heading,
  subtext,
  backgroundImage,
  backgroundImageMobile,
  focalPoint,
  overlayOpacity,
  ctaButtons,
  rightSlot,
}) {
  const hasRight = Boolean(rightSlot);
  const opacity = Number.isFinite(Number(overlayOpacity))
    ? Math.max(0, Math.min(1, Number(overlayOpacity)))
    : DEFAULT_OVERLAY;
  const ctas = Array.isArray(ctaButtons) ? ctaButtons : [];

  return (
    <section
      className={`relative isolate overflow-hidden bg-[#2a120d] pt-24 text-white lg:pt-28 min-h-[60svh] lg:min-h-[72vh] ${
        hasRight ? "" : "lg:max-h-[720px]"
      }`}
    >
      {backgroundImage ? (
        <picture>
          {backgroundImageMobile ? (
            <source srcSet={backgroundImageMobile} media="(max-width: 767px)" />
          ) : null}
          <img
            src={backgroundImage}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: focalPointStyle(focalPoint) }}
          />
        </picture>
      ) : null}
      <div
        data-banner-overlay=""
        aria-hidden="true"
        className="absolute inset-0 bg-[#2a120d]"
        style={{ opacity }}
      />
      <div
        data-banner-layout={hasRight ? "split" : "single"}
        className={
          hasRight
            ? "container relative z-10 mx-auto grid items-end gap-10 px-4 pb-12 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8 min-h-[calc(60svh-6rem)] lg:min-h-[calc(72vh-7rem)]"
            : "container relative z-10 mx-auto flex items-end px-4 pb-12 sm:px-6 lg:px-8 min-h-[calc(60svh-6rem)] lg:min-h-[calc(72vh-7rem)]"
        }
      >
        <div className={hasRight ? "" : "max-w-4xl"}>
          {eyebrow ? (
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.28em] text-[#f5c45e]">
              {eyebrow}
            </p>
          ) : null}
          {heading ? (
            <h1 className="max-w-4xl text-3xl font-semibold leading-tight md:text-6xl">
              {heading}
            </h1>
          ) : null}
          {subtext ? (
            <p className="mt-6 max-w-3xl text-lg leading-8 text-white/82">{subtext}</p>
          ) : null}
          {ctas.length > 0 ? (
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {ctas.map((cta, index) => (
                <CtaButton key={`${cta.label || ""}-${index}`} cta={cta} />
              ))}
            </div>
          ) : null}
        </div>
        {hasRight ? <div className="min-w-0">{rightSlot}</div> : null}
      </div>
    </section>
  );
}
